import { FunctionComponent, HTMLAttributes, ReactNode, useContext, useEffect } from 'react';
import {
  PassiveComponentContext,
  useAudioTrigger,
  L12nText,
  useMedia,
  useIsLargerThanWide,
  useIsPortrait,
  useIsLandscape,
  useIsLargerThanMediumWide,
} from '@pp/core';
import { useTranslationText } from '../../hooks/useTranslationText';
import styles from './WinResult.module.css';
import useWindowDimensions from '../../hooks/useWindowDimension';
import { MWSpecificGameStatus } from '../../store/slices/mwState/types';
export interface WinResultProp extends HTMLAttributes<HTMLDivElement> {
  winAmount?: string | ReactNode;
  result?: string | null;
  winType?: string | null;
  hideWinMessage?: boolean | false;
}

export const WinResult: FunctionComponent<WinResultProp> = ({ winAmount, result, winType, hideWinMessage }) => {
  const { height } = useWindowDimensions();
  const isStandardWidth = useIsLargerThanWide();
  const isPortrait = useIsPortrait();
  const isLandscape = useIsLandscape();
  const isWideWidth = useIsLargerThanMediumWide();
  const isExtraWideWidth = useMedia('(min-width: 1512px)');
  const audioTrigger = useAudioTrigger();
  const isPassive = useContext(PassiveComponentContext);
  const gameStatus = winType ?? '';
  const styleResult = `result_${result}_prizeText`;
  const styleWinStarContainer = `starcontainer_${gameStatus.toLowerCase()}`;
  const styleWinStar = `${gameStatus.toLowerCase()}_star`;
  const winAmountClass = gameStatus === 'MEGA_WIN' ? styles.megaWinAmount : styles.youWinAmount;

  useEffect(() => {
    if (isPassive) return;
    if (gameStatus === MWSpecificGameStatus.MEGA_WIN) {
      audioTrigger.play({ name: 'megaWin' });
    } else if (gameStatus === MWSpecificGameStatus.YOU_WIN) {
      audioTrigger.play({ name: 'win' });
    }
  }, [audioTrigger, isPassive, gameStatus]);
  const translateKey = gameStatus === 'MEGA_WIN' ? 'MEGAWIN_CAPS' : gameStatus;
  const translatedWinMessage = useTranslationText(translateKey);

  const shadowOffsetConditions1 = [
    { conditions: [isExtraWideWidth], value: 0.75 },
    { conditions: [isWideWidth], value: 0.75 },
    { conditions: [isStandardWidth], value: 0.7 },
    { conditions: [isPortrait], value: 0.6 },
    { conditions: [isLandscape], value: 0.8 },
  ];
  const shadowOffsetConditions2 = [
    { conditions: [isExtraWideWidth], value: 0.98 },
    { conditions: [isWideWidth], value: 1 },
    { conditions: [isStandardWidth], value: 0.85 },
    { conditions: [isPortrait], value: 0.8 },
    { conditions: [isLandscape], value: 1.1 },
  ];
  const shadowOffsetConditions3 = [
    { conditions: [isExtraWideWidth], value: 1 },
    { conditions: [isWideWidth], value: 1.1 },
    { conditions: [isStandardWidth], value: 0.9 },
    { conditions: [isPortrait], value: 0.8 },
    { conditions: [isLandscape], value: 1.1 },
  ];
  const matchingCondition = (shadowOffsetConditions: { conditions: boolean[]; value: number }[]): any => {
    return shadowOffsetConditions.find((condition) => {
      return condition.conditions.every((c) => c);
    })?.value;
  };
  const generateTextShadow = (color: string, r: any, maxR: number): string => {
    let textShadow = '';
    r = Math.min(r, maxR);
    const n = Math.ceil(4 * Math.PI * r);
    for (let i = 0; i < n; i++) {
      const theta = (2 * Math.PI * i) / n;
      const x = r * Math.cos(theta);
      const y = r * Math.sin(theta);
      textShadow += `${x}px ${y}px 0 ${color}${i === n - 1 ? '' : ','}`;
    }
    return textShadow;
  };
  const statusMsgStyle1: any = {
    '--statusMsgshadow1': generateTextShadow(
      'var(--game-winner-text-shadow)',
      (matchingCondition(shadowOffsetConditions1) * height) / 100,
      10
    ),
    '--statusMsgshadow2': generateTextShadow(
      '#ffb800',
      (matchingCondition(shadowOffsetConditions2) * height) / 100,
      13
    ),
    '--statusMsgshadow3': generateTextShadow(
      '#c67802',
      (matchingCondition(shadowOffsetConditions3) * height) / 100,
      13
    ),
  };
  const commonContent = (
    <div style={statusMsgStyle1} className={styles.statusMsg} data-text={translatedWinMessage}>
      <span data-text={translatedWinMessage}>
        <L12nText tk={translateKey} />
      </span>
    </div>
  );

  const winMessageDisplayStyle =
    (gameStatus === MWSpecificGameStatus.YOU_WIN || gameStatus === MWSpecificGameStatus.MEGA_WIN) && !hideWinMessage
      ? ''
      : styles.hide;

  return (
    <>
      <div data-test-id={`popup_${gameStatus}`} className={`${styles.winMessage} ${winMessageDisplayStyle}`}>
        <div className={styles.winMessageChild}>
          <div className={styles.Ring}>
            <div className={styles.outerRing}></div>
            <div className={styles.bulbs}>
              <div className={`${styles.bulb} ${styles.bulb1}`}></div>
              <div className={`${styles.bulb} ${styles.bulb2}`}></div>
              <div className={`${styles.bulb} ${styles.bulb3}`}></div>
              <div className={`${styles.bulb} ${styles.bulb4}`}></div>
              <div className={`${styles.bulb} ${styles.bulb5}`}></div>
              <div className={`${styles.bulb} ${styles.bulb6}`}></div>
              <div className={`${styles.bulb} ${styles.bulb7}`}></div>
            </div>
          </div>
          <div className={`${styles.winnerText} ${styles.winnerTextYouWin}`}>{commonContent}</div>
          <div className={`${styles[styleWinStarContainer]}`}>
            <div className={`${styles[styleWinStar]} ${styles[styleWinStar + '1']}`}></div>
            <div className={`${styles[styleWinStar]} ${styles[styleWinStar + '2']}`}></div>
            <div className={`${styles[styleWinStar]} ${styles[styleWinStar + '3']}`}></div>
            <div className={`${styles[styleWinStar]} ${styles[styleWinStar + '4']}`}></div>
          </div>
          <div className={styles.winResultamount}>
            <div className={styles.winResultamountChild}>
              <div className={styles.topline}></div>
              <div className={styles.bottomline}></div>
              <div className={`${styles.amount} ${winAmountClass}`}>{winAmount}</div>
            </div>
          </div>
        </div>
      </div>
      {gameStatus.startsWith('RESULT_') && !hideWinMessage && (
        <div data-test-id={'popup_result'} className={styles.containerWrapper}>
          <div className={styles.containerWrapperChild}>
            <div className={styles.resultContainer}>
              <div className={`${styles.result__wrapper} ${styles.result} ${styles[styleResult]} `}>
                <div className={styles.digit}>{result}</div>
              </div>
            </div>
            <div className={styles.resultDiv}>
              <div className={styles.topbar}></div>
              <div className={styles.bottombar}></div>
              <div className={styles.message}>
                <L12nText key="RESULT_POPUP" tk="RESULT" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
